import { ColDef, ITooltipParams } from 'ag-grid-community';
import { FC, useMemo } from 'react';
import {
  DealSecurityJson,
  IFinanceRoundDataModel,
  isConvertibleRound,
} from '../../../data-models/finance-round.data-model';
import { AgTable } from '../../../components/AgTable/AgTable';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import { FMT } from '../../../util/formatter-service';
import {
  CurrencyCompact2dpFormatterDataModel,
  Pps2dpFormatterDataModel,
  PpsFormatterDataModel,
} from '../../../util/formatters/NumericFormatters';
import { CollapsibleSectionCard } from './CollapsibleSectionCard';
import {
  gridAutoCss,
  gridHeaderEllipsisCss,
  gridHugRowsCss,
  gridOverlayMutedCss,
} from './deals-detail.styles';

export interface IRoundSecurityRow {
  security: string | null;
  sharesIssuedOrConverted: number | null;
  pps: number | null; // price per share
  amountRaised: number | null;
  pctOfRound: number | null; // percentage points (90.02 renders as 90.02%)
}

/**
 * Security Overview rows: one row per security issued in the round (round-wide,
 * all holders), from `round.dealSecurities`. Null/absent means the backend has
 * no securities recorded for the round — the empty-state overlay shows.
 *
 * Amount Raised is shares × PPS (the BE sends only the inputs); % of Round divides
 * by the sum of the computed amounts so the column always totals 100%, even when
 * that sum drifts from the round's stated raiseAmount.
 */
export function toSecurityRows(dealSecurities: DealSecurityJson[] | null | undefined): IRoundSecurityRow[] {
  const securities = dealSecurities ?? [];
  const amounts = securities.map((s) =>
    s.sharesIssued != null && s.pricePerShare != null ? s.sharesIssued * s.pricePerShare : null
  );
  const total = amounts.reduce<number>((sum, amount) => sum + (amount ?? 0), 0);
  return securities.map((s, i) => ({
    security: s.securityName,
    sharesIssuedOrConverted: s.sharesIssued,
    pps: s.pricePerShare,
    amountRaised: amounts[i],
    pctOfRound: amounts[i] != null && total > 0 ? (amounts[i] / total) * 100 : null,
  }));
}

const rightAligned = { textAlign: 'right' as const };

// Built at render time: AgFormatterService is not initialized at module load.
// Exported so tests can assert the PPS tooltip without mounting a grid.
export function useSecurityColDefs(): ColDef<IRoundSecurityRow>[] {
  return useMemo(
    () => [
      { headerName: 'Security', field: 'security', flex: 2, minWidth: 200, headerTooltip: 'Security' },
      {
        headerName: 'Shares Issued/Converted',
        field: 'sharesIssuedOrConverted',
        flex: 1,
        minWidth: 150,
        cellStyle: rightAligned,
        headerTooltip: 'Shares Issued/Converted',
        valueFormatter: AgFormatterService.get().getFormatterForId('integer'),
      },
      {
        headerName: 'PPS',
        field: 'pps',
        flex: 1,
        minWidth: 90,
        cellStyle: rightAligned,
        headerTooltip: 'PPS',
        // getFormatterForModel is not generic over the row type; cast the colDef
        // (same pattern as Finance2/Security/useLedgerColDefs).
        valueFormatter: AgFormatterService.get().getFormatterForModel(Pps2dpFormatterDataModel),
        // A PPS can carry more precision than the 2dp cell shows, so reveal the full
        // figure on hover. Silent when 2dp already tells the whole story.
        tooltipValueGetter: ({ value }: ITooltipParams<IRoundSecurityRow, number | null>) =>
          value != null && Number(value.toFixed(2)) !== value ? FMT.format(PpsFormatterDataModel, value) : '',
      } as ColDef<IRoundSecurityRow>,
      {
        headerName: 'Amount Raised',
        field: 'amountRaised',
        flex: 1,
        minWidth: 130,
        cellStyle: rightAligned,
        headerTooltip: 'Amount Raised',
        valueFormatter: AgFormatterService.get().getFormatterForModel(CurrencyCompact2dpFormatterDataModel),
        // Abbreviated in the cell; exact to the cent on hover (same as Round Overview).
        tooltipValueGetter: ({ value }: ITooltipParams<IRoundSecurityRow, number | null>) =>
          value != null ? FMT.format('currency', value) : '',
      } as ColDef<IRoundSecurityRow>,
      {
        headerName: '% of Round',
        field: 'pctOfRound',
        flex: 1,
        minWidth: 110,
        cellStyle: rightAligned,
        headerTooltip: '% of Round',
        valueFormatter: AgFormatterService.get().getFormatterForId('percent'),
      },
    ],
    []
  );
}

interface RoundSecurityGridProps {
  round: IFinanceRoundDataModel;
}

/**
 * Security Overview section of the Deals-tab detail pane: the security classes
 * issued in the selected round (round-wide), with shares and price per share.
 */
export const RoundSecurityGrid: FC<RoundSecurityGridProps> = ({ round }) => {
  const columnDefs = useSecurityColDefs();

  const rows = useMemo(() => toSecurityRows(round.dealSecurities), [round.dealSecurities]);

  return (
    <CollapsibleSectionCard
      title='Security Overview'
      isEmpty={rows.length === 0}
      emptyAnnotation='No securities'
      defaultCollapsed={isConvertibleRound(round)}
    >
      <div css={[gridAutoCss, gridHeaderEllipsisCss, gridOverlayMutedCss, rows.length > 0 && gridHugRowsCss]}>
        <AgTable
          columnDefs={columnDefs}
          rowData={rows}
          domLayout='autoHeight'
          defaultColDef={{
            flex: 1,
            suppressSizeToFit: false,
            wrapHeaderText: false,
            autoHeaderHeight: false,
          }}
          rowGroupPanelShow='never'
          overlayNoRowsTemplate='No securities recorded for this round.'
        />
      </div>
    </CollapsibleSectionCard>
  );
};
